import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Search, Edit, Trash2, Key, Eye, ChevronLeft, ChevronRight,
  BookOpen, Clock, Award, FileText, User, Mail, Phone, Globe
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Students Tab - Admin panel for managing students
 */
const StudentsTab = () => {
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  
  const limit = 10;

  // Fetch students
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit
      });
      if (search) params.append('search', search);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/students?${params}`,
        { withCredentials: true }
      );
      setStudents(response.data.students);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Handle search with debounce
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  // Create student
  const handleCreateStudent = async (formData) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/students`,
        formData,
        { withCredentials: true }
      );
      
      if (response.data.temporary_password) {
        setNewPassword(response.data.temporary_password);
        setShowPasswordModal(true);
      }
      
      toast.success('Student created successfully');
      setShowCreateModal(false);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create student');
    }
  };

  // View student details
  const handleViewStudent = async (studentId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/students/${studentId}`,
        { withCredentials: true }
      );
      setSelectedStudent(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load student details');
    }
  };

  // Reset password
  const handleResetPassword = async (studentId) => {
    if (!window.confirm('Reset this student\'s password?')) return;
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/students/${studentId}/reset-password`,
        {},
        { withCredentials: true }
      );
      setNewPassword(response.data.temporary_password);
      setShowPasswordModal(true);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  // Deactivate student
  const handleDeactivate = async (studentId) => {
    if (!window.confirm('Deactivate this student account?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/students/${studentId}`,
        { withCredentials: true }
      );
      toast.success('Student deactivated');
      fetchStudents();
    } catch (error) {
      toast.error('Failed to deactivate student');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="bg-white rounded-xl shadow-sm" data-testid="students-tab">
      {/* Header */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Student Management</h2>
            <p className="text-sm text-slate-500 mt-1">
              Manage student accounts, track progress, and assign homework
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 transition-colors"
            data-testid="create-student-btn"
          >
            <Plus className="w-4 h-4" />
            Add Student
          </button>
        </div>
        
        {/* Search */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            data-testid="search-students"
          />
        </div>
      </div>

      {/* Students List */}
      <div className="divide-y divide-slate-100">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto" />
          </div>
        ) : students.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            No students found
          </div>
        ) : (
          students.map((student) => (
            <div
              key={student.id}
              className="p-4 hover:bg-slate-50 transition-colors"
              data-testid={`student-row-${student.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-slate-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">{student.name}</h3>
                    <p className="text-sm text-slate-500">{student.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="hidden md:flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      {student.completed_classes || 0} classes
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      student.is_active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {student.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleViewStudent(student.id)}
                      className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                      title="View Details"
                      data-testid={`view-student-${student.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleResetPassword(student.id)}
                      className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Reset Password"
                      data-testid={`reset-password-${student.id}`}
                    >
                      <Key className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeactivate(student.id)}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      title="Deactivate"
                      data-testid={`deactivate-student-${student.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-slate-200 flex items-center justify-between">
          <span className="text-sm text-slate-500">
            Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-slate-600">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Create Student Modal */}
      {showCreateModal && (
        <CreateStudentModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateStudent}
        />
      )}

      {/* Student Detail Modal */}
      {showDetailModal && selectedStudent && (
        <StudentDetailModal
          student={selectedStudent}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedStudent(null);
          }}
          onRefresh={() => handleViewStudent(selectedStudent.student.id)}
        />
      )}

      {/* Password Display Modal */}
      {showPasswordModal && (
        <PasswordDisplayModal
          password={newPassword}
          onClose={() => {
            setShowPasswordModal(false);
            setNewPassword('');
          }}
        />
      )}
    </div>
  );
};


/**
 * Create Student Modal
 */
const CreateStudentModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({
    email: '',
    name: '',
    phone: '',
    preferred_language: 'en',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await onSubmit(form);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md" data-testid="create-student-modal">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Create Student Account</h3>
          <p className="text-sm text-slate-500 mt-1">
            A temporary password will be generated for the student
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Email *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900"
                placeholder="student@example.com"
                data-testid="create-student-email"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Name *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900"
                placeholder="Student Name"
                data-testid="create-student-name"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Phone
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900"
                placeholder="+1 234 567 8900"
                data-testid="create-student-phone"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Preferred Language
            </label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <select
                value={form.preferred_language}
                onChange={(e) => setForm({ ...form, preferred_language: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900 appearance-none"
                data-testid="create-student-language"
              >
                <option value="en">English</option>
                <option value="ko">Korean</option>
                <option value="ja">Japanese</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Admin Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900 h-24 resize-none"
              placeholder="Internal notes about this student..."
              data-testid="create-student-notes"
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50"
              data-testid="create-student-submit"
            >
              {submitting ? 'Creating...' : 'Create Student'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


/**
 * Student Detail Modal
 */
const StudentDetailModal = ({ student, onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const { student: studentInfo, progress, homework, class_notes } = student;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="student-detail-modal">
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-slate-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{studentInfo.name}</h3>
              <p className="text-sm text-slate-500">{studentInfo.email}</p>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-4 mt-4">
            {['overview', 'progress', 'homework', 'notes'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors capitalize ${
                  activeTab === tab
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <BookOpen className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{progress?.lessons_completed || 0}</p>
                  <p className="text-sm text-slate-600">Classes</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <Clock className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{progress?.total_hours || 0}</p>
                  <p className="text-sm text-slate-600">Hours</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <Award className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900 capitalize">{progress?.current_level || 'Beginner'}</p>
                  <p className="text-sm text-slate-600">Level</p>
                </div>
              </div>
              
              {/* Details */}
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Phone</span>
                  <span className="font-medium text-slate-900">{studentInfo.phone || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Language</span>
                  <span className="font-medium text-slate-900 capitalize">{studentInfo.preferred_language || 'en'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Status</span>
                  <span className={`font-medium ${studentInfo.is_active ? 'text-green-600' : 'text-slate-400'}`}>
                    {studentInfo.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-600">Joined</span>
                  <span className="font-medium text-slate-900">
                    {new Date(studentInfo.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              
              {/* Admin Notes */}
              {studentInfo.admin_notes && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-900 mb-2">Admin Notes</h4>
                  <p className="text-sm text-slate-600">{studentInfo.admin_notes}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'progress' && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-2">Current Level</h4>
                <p className="text-lg capitalize">{progress?.current_level || 'Beginner'}</p>
              </div>
              
              {progress?.goals?.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-900 mb-2">Learning Goals</h4>
                  <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                    {progress.goals.map((goal) => (
                      <li key={`goal-${goal}`}>{goal}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {progress?.achievements?.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-900 mb-2">Achievements</h4>
                  <div className="flex flex-wrap gap-2">
                    {progress.achievements.map((achievement) => (
                      <span
                        key={`achievement-${achievement}`}
                        className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm"
                      >
                        {achievement}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'homework' && (
            <div className="space-y-3">
              {homework?.length > 0 ? (
                homework.map((hw) => (
                  <div
                    key={hw.id}
                    className="border border-slate-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-slate-900">{hw.title}</h4>
                        <p className="text-sm text-slate-500 mt-1">{hw.description}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        hw.status === 'completed' ? 'bg-green-100 text-green-700' :
                        hw.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {hw.status}
                      </span>
                    </div>
                    {hw.due_date && (
                      <p className="text-xs text-slate-400 mt-2">
                        Due: {new Date(hw.due_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-500 py-8">No homework assigned</p>
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-3">
              {class_notes?.length > 0 ? (
                class_notes.map((note) => (
                  <div
                    key={note.id}
                    className="border border-slate-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-900">
                        {note.teacher_name || 'Teacher'}
                      </span>
                      {note.performance_rating && (
                        <span className="text-sm text-amber-600">
                          {'★'.repeat(note.performance_rating)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">{note.notes}</p>
                    {note.class_date && (
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(note.class_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-500 py-8">No class notes yet</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};


/**
 * Password Display Modal
 */
const PasswordDisplayModal = ({ password, onClose }) => {
  const copyPassword = () => {
    navigator.clipboard.writeText(password);
    toast.success('Password copied to clipboard');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-sm" data-testid="password-modal">
        <div className="p-6 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Key className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Temporary Password</h3>
          <p className="text-sm text-slate-500 mb-4">
            Share this password with the student. They should change it upon first login.
          </p>
          
          <div 
            className="bg-slate-100 rounded-lg p-3 font-mono text-lg text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
            onClick={copyPassword}
            data-testid="temp-password"
          >
            {password}
          </div>
          <p className="text-xs text-slate-400 mt-2">Click to copy</p>
          
          <button
            onClick={onClose}
            className="w-full mt-6 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentsTab;
